export type ClusterListTrip = {
  tripKey: string;
  vehicleLabel: string;
  tripNumber: number;
  color: string;
};

// Same shape as the fleet map's cluster popup: the assets sitting under the
// badge, listed out, each one a way into that route. That map already
// teaches people this pattern, so there's nothing new to learn here.
export const createClusterAssetListOverlay = ({
  google,
  map,
  position,
  trips,
  heading,
  groupRows = false,
  onViewDetails,
  onHoverTrip,
  onClose,
}: {
  google: any;
  map: any;
  position: { lat: number; lng: number };
  trips: ClusterListTrip[];
  // defaults to the fleet-map wording; the multi-day view overrides it
  // since "assets" there is one asset over several days
  heading?: string;
  // collapses the repeated label into a subheading with its trips beneath.
  // One asset over a week puts the same handful of day labels on dozens of
  // rows, and reading "Sep 01, 2026 · Trip 2" forty times is worse than
  // reading "Sep 01, 2026" once with its trips under it.
  groupRows?: boolean;
  onViewDetails: (tripKey: string) => void;
  onHoverTrip: (tripKey: string | null) => void;
  onClose: () => void;
}) => {
  class ClusterAssetListOverlay extends google.maps.OverlayView {
    container: HTMLDivElement | null = null;
    // Where the popup has been dragged to, in pixels from where it would
    // otherwise sit. Held across redraws, so panning the map carries it
    // along rather than snapping it back over the badge.
    dragOffsetX = 0;
    dragOffsetY = 0;
    // set while the cursor is over the popup, so the map's own gestures can
    // be put back exactly as they were
    gestureHandlingToRestore: string | null = null;
    releaseDrag: (() => void) | null = null;

    takeMapGestures() {
      if (this.gestureHandlingToRestore !== null) return;
      this.gestureHandlingToRestore = map.get("gestureHandling") ?? "auto";
      map.setOptions({ gestureHandling: "none" });
    }

    returnMapGestures() {
      if (this.gestureHandlingToRestore === null) return;
      map.setOptions({ gestureHandling: this.gestureHandlingToRestore });
      this.gestureHandlingToRestore = null;
    }

    onAdd() {
      const container = document.createElement("div");
      container.style.cssText = `
        position: absolute;
        transform: translate(-50%, calc(-100% - 22px));
        width: min(360px, calc(100vw - 24px));
        max-height: min(360px, calc(100vh - 24px));
        background: #ffffff;
        border-radius: 10px;
        box-shadow: 0 6px 24px rgba(0,0,0,0.22);
        overflow: hidden;
        font-family: inherit;
      `;
      // the badge is clickable and sits under this, so clicks must not fall
      // through to the map's own close-on-click handler
      container.addEventListener("click", (event) => event.stopPropagation());
      container.addEventListener("mousedown", (event) =>
        event.stopPropagation()
      );

      // The list scrolls, but Google's wheel handler sits on an ancestor of
      // this overlay and runs first, so stopping the event here is too late
      // to keep the map from zooming under it. Taking the map's gestures
      // away while the cursor is over the popup is what actually lets the
      // list scroll - which is why a list past a handful of rows used to
      // zoom the map instead of moving.
      container.addEventListener("wheel", (event) => event.stopPropagation());
      container.addEventListener("mouseenter", () => this.takeMapGestures());
      container.addEventListener("mouseleave", () => this.returnMapGestures());

      const header = document.createElement("div");
      header.style.cssText = `
        display: flex; align-items: center; justify-content: space-between;
        gap: 8px; padding: 8px 10px; border-bottom: 1px solid #eef0f3;
        cursor: move; user-select: none;
      `;
      // the popup covers the road it is describing, so it has to be possible
      // to push it aside without closing it
      header.addEventListener("mousedown", (event) => {
        if ((event.target as HTMLElement).closest("button")) return;
        event.preventDefault();
        const startX = event.clientX;
        const startY = event.clientY;
        const originX = this.dragOffsetX;
        const originY = this.dragOffsetY;
        const onMove = (moveEvent: MouseEvent) => {
          this.dragOffsetX = originX + moveEvent.clientX - startX;
          this.dragOffsetY = originY + moveEvent.clientY - startY;
          this.draw();
        };
        const onUp = () => this.releaseDrag?.();
        this.releaseDrag = () => {
          window.removeEventListener("mousemove", onMove);
          window.removeEventListener("mouseup", onUp);
          this.releaseDrag = null;
        };
        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);
      });
      const title = document.createElement("span");
      title.textContent = heading ?? `${trips.length} trips share this road`;
      title.style.cssText = "font-size: 12px; font-weight: 700; color: #1f2937;";
      const closeButton = document.createElement("button");
      closeButton.type = "button";
      closeButton.setAttribute("aria-label", "Close");
      closeButton.textContent = "×";
      closeButton.style.cssText = `
        border: 0; background: none; cursor: pointer; line-height: 1;
        font-size: 17px; color: #9aa1ab; padding: 0 2px;
      `;
      closeButton.addEventListener("click", onClose);
      header.append(title, closeButton);

      const list = document.createElement("div");
      list.style.cssText = "max-height: min(216px, calc(100vh - 112px)); overflow-y: auto;";

      const appendRow = (trip: ClusterListTrip, grouped: boolean) => {
        const row = document.createElement("div");
        row.style.cssText = `
          display: flex; align-items: center; gap: 8px;
          padding: 7px 10px; padding-left: ${grouped ? 20 : 10}px;
          cursor: pointer; border-bottom: 1px solid #f4f5f7;
        `;
        row.addEventListener("mouseenter", () => {
          row.style.background = "#f3f6fb";
          onHoverTrip(trip.tripKey);
        });
        row.addEventListener("mouseleave", () => {
          row.style.background = "transparent";
          onHoverTrip(null);
        });
        row.addEventListener("click", () => onViewDetails(trip.tripKey));

        const swatch = document.createElement("span");
        swatch.style.cssText = `
          width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0;
          background: ${trip.color};
        `;

        const label = document.createElement("span");
        label.style.cssText = `
          flex: 1 1 auto; min-width: 0; font-size: 12px; color: #1f2937;
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        `;
        // the group subheading already carries the label, so a grouped row
        // only needs to say which trip
        label.textContent = grouped
          ? `Trip ${trip.tripNumber}`
          : `${trip.vehicleLabel} · Trip ${trip.tripNumber}`;
        label.title = label.textContent;

        const action = document.createElement("span");
        action.style.cssText =
          "font-size: 11px; font-weight: 600; color: #2f6feb; flex-shrink: 0;";
        action.textContent = "View details";

        row.append(swatch, label, action);
        list.appendChild(row);
      };

      if (groupRows) {
        // insertion-ordered, so groups appear in whatever order the caller
        // handed them over rather than alphabetically by label
        const byLabel = new Map<string, ClusterListTrip[]>();
        trips.forEach((trip) => {
          const bucket = byLabel.get(trip.vehicleLabel);
          if (bucket) bucket.push(trip);
          else byLabel.set(trip.vehicleLabel, [trip]);
        });

        byLabel.forEach((groupTrips, groupLabel) => {
          const subheading = document.createElement("div");
          subheading.style.cssText = `
            display: flex; align-items: center; justify-content: space-between;
            gap: 8px; padding: 5px 10px; background: #f7f8fa;
            border-bottom: 1px solid #eef0f3;
            font-size: 11px; font-weight: 700; color: #4b5563;
            position: sticky; top: 0;
          `;
          const groupName = document.createElement("span");
          groupName.textContent = groupLabel;
          const groupCount = document.createElement("span");
          groupCount.style.cssText = "font-weight: 600; color: #9aa1ab;";
          groupCount.textContent = `${groupTrips.length} trip${groupTrips.length === 1 ? "" : "s"}`;
          subheading.append(groupName, groupCount);
          list.appendChild(subheading);

          groupTrips.forEach((trip) => appendRow(trip, true));
        });
      } else {
        trips.forEach((trip) => appendRow(trip, false));
      }

      container.append(header, list);
      this.container = container;
      this.getPanes().floatPane.appendChild(container);
    }

    draw() {
      if (!this.container) return;
      const projection = this.getProjection();
      if (!projection) return;
      const latLng = new google.maps.LatLng(position.lat, position.lng);
      // Two different pixel spaces, and the clamp below used to mix them.
      // The popup lives in floatPane, so its left/top are measured from
      // that pane's origin - which is NOT the map container's top-left, and
      // slides as the map pans and zooms. The edges it has to stay inside
      // are the container's, measured from 0. Comparing a pane coordinate
      // (often negative, since the pane is laid out around the map centre)
      // against 0..clientWidth meant the lower bound almost always won:
      // dragging left just pushed against a floor that never moved, and
      // dragging right only did anything once the pane coordinate happened
      // to climb past it - which is why a zoom, moving the pane origin,
      // changed which direction worked.
      //
      // So the clamp is done in container space and converted back.
      const divPoint = projection.fromLatLngToDivPixel(latLng);
      const containerPoint = projection.fromLatLngToContainerPixel(latLng);
      if (!divPoint || !containerPoint) return;
      const paneOffsetX = divPoint.x - containerPoint.x;
      const paneOffsetY = divPoint.y - containerPoint.y;

      const mapDiv = map.getDiv();
      const width = this.container.offsetWidth;
      const height = this.container.offsetHeight;
      const halfWidth = width / 2;
      const padding = 12;

      const left = Math.max(
        halfWidth + padding,
        Math.min(
          containerPoint.x + this.dragOffsetX,
          mapDiv.clientWidth - halfWidth - padding
        )
      );
      const aboveTop = containerPoint.y - height - 22;
      const top = Math.min(
        Math.max(
          (aboveTop >= padding ? aboveTop : containerPoint.y + 22) +
            this.dragOffsetY,
          padding
        ),
        Math.max(padding, mapDiv.clientHeight - height - padding)
      );

      this.container.style.transform = "translate(-50%, 0)";
      this.container.style.left = `${left + paneOffsetX}px`;
      this.container.style.top = `${top + paneOffsetY}px`;
    }

    onRemove() {
      onHoverTrip(null);
      // mouseleave never arrives when the popup is closed from under the
      // cursor, which would leave the map with its gestures switched off
      this.returnMapGestures();
      this.releaseDrag?.();
      this.container?.parentNode?.removeChild(this.container);
      this.container = null;
    }
  }

  return new ClusterAssetListOverlay();
};
